import { useMemo } from 'react';
import { css } from '@emotion/react';
import { DeploymentState } from 'common/CommonConstants';
import { StatusItemState } from 'common/status/StatusDocument';
import type { StatusTree } from 'common/status/StatusTree';
import { EllipsisLoader } from 'client/components/Other/EllipsisLoader';
import { Skeleton } from 'client/components/Skeleton';
import { statusItemStateToTagProps } from 'client/components/Status/statusUtils';
import { StatusTreeTagIcon } from 'client/components/StatusTree/components/StatusTreeTagIcon';
import { Tag } from 'client/components/Tag';

export interface DeploymentStatusTagProps {
  statusTree: StatusTree | null | undefined;
}

export const DeploymentStatusTag = ({ statusTree }: DeploymentStatusTagProps) => {
  let itemStateID = statusTree?.itemStateID || StatusItemState.GRAY;
  let deploymentInfo: string | JSX.Element = '';

  if (!statusTree || statusTree.deploymentStateID === DeploymentState.DELETED) {
    return <Skeleton height={26} width={82} />;
  }

  if (statusTree) {
    if (statusTree.deploymentStateID === DeploymentState.DELETED_PENDING) {
      itemStateID = StatusItemState.RED;
      deploymentInfo = (
        <>
          Deleting
          <EllipsisLoader />
        </>
      );
    } else if (statusTree.deploymentStateID === DeploymentState.CLONE_PENDING) {
      deploymentInfo = (
        <>
          Cloning
          <EllipsisLoader />
        </>
      );
    } else if (statusTree.deploymentStateID === DeploymentState.STOPPED) {
      itemStateID = StatusItemState.GRAY;
      deploymentInfo = 'Stopped';
    } else if (statusTree.itemStateID === StatusItemState.GREEN) {
      deploymentInfo = 'Running';
    } else if (statusTree.isStarting) {
      itemStateID = StatusItemState.YELLOW;
      deploymentInfo = (
        <>
          Starting cluster
          <EllipsisLoader />
        </>
      );
      // TODO: There could be another message after core is connected, "Initializing" ?
      // TODO: We should not show "Starting" if eg. 10 minutes has passed and we never reach green
    } else if (statusTree.itemStateID === StatusItemState.YELLOW) {
      deploymentInfo = 'Possible issues';
    } else if (statusTree.itemStateID === StatusItemState.RED) {
      deploymentInfo = 'Error';
    }
  }

  const tagProps = useMemo(
    () => ({
      ...statusItemStateToTagProps[itemStateID],
      children: (
        <>
          <StatusTreeTagIcon itemStateID={itemStateID} isReady={true} />
          {deploymentInfo}
        </>
      ),
    }),
    [itemStateID, deploymentInfo]
  );

  return <Tag {...tagProps} css={statusTagStyle} />;
};

const statusTagStyle = css`
  padding: 4px 7px;

  > svg {
    margin-left: -1px;
    margin-right: 4px;
    transform: translateY(-1px);
  }
`;
